import React, { useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronUp, X } from 'lucide-react';
import { stageIcon } from '../Modal/RemediationProgressModal';
import { RemediationJob } from '../../providers/RemediationJobProvider';
import { RemediationItemProgress } from '../../modules/VulnerabilityCenter/Utils/types';

interface RemediationProgressTrayProps {
  job: RemediationJob;
  onStop: () => void;
  onDismiss: () => void;
}

const isFinished = (item: RemediationItemProgress) => item.stage === 'success' || item.stage === 'skipped' || item.stage === 'failed';

const STEPS = [
  { key: 'checking', label: 'Check' },
  { key: 'review', label: 'Review' },
  { key: 'creating', label: 'Create' },
] as const;

const getSummary = (job: RemediationJob) => {
  const noun = job.kind === 'os' ? 'OS update' : 'vulnerability';
  const nounPlural = job.kind === 'os' ? 'OS updates' : 'vulnerabilities';

  if (job.phase === 'checking') {
    const total = job.checkItems.length;
    return {
      title: 'Preparing your remediation',
      detail: `Checking ${total === 1 ? `1 ${noun}` : `${total} ${nounPlural}`} to see what needs updating. You can keep working. We'll ask you to review the plans when they're ready.`,
      done: job.checkItems.filter(isFinished).length,
      total,
      unit: 'checked',
    };
  }

  if (job.phase === 'creating') {
    return {
      title: 'Creating your remediation plans',
      detail: "You can keep working. We'll notify you when this is finished.",
      done: job.planItems.filter(isFinished).length,
      total: job.planItems.length,
      unit: 'created',
    };
  }

  const created = job.planItems.filter((item) => item.stage === 'success').length;
  const failed = job.planItems.filter((item) => item.stage === 'failed').length;

  if (job.planItems.length === 0) {
    return { title: 'No remediation plans needed', detail: "Here's what we found for each item.", done: 1, total: 1, unit: '' };
  }

  return {
    title: failed > 0 ? 'Remediation finished with problems' : 'Remediation plans created',
    detail: `${created} created${failed > 0 ? `, ${failed} failed` : ''}.${created > 0 ? ' Created plans are in Patch Center.' : ''}`,
    done: job.planItems.length,
    total: job.planItems.length,
    unit: '',
  };
};

// The floating card that follows the user around the app while a remediation job runs.
const RemediationProgressTray: React.FC<RemediationProgressTrayProps> = ({ job, onStop, onDismiss }) => {
  const [expanded, setExpanded] = useState(true);

  const summary = getSummary(job);
  const isDone = job.phase === 'done';
  const hasFailures = job.planItems.some((item) => item.stage === 'failed');
  const percent = summary.total > 0 ? Math.round((summary.done / summary.total) * 100) : 0;
  const items = job.planItems.length > 0 ? job.planItems : job.checkItems;
  const activeStep = isDone ? STEPS.length : STEPS.findIndex((step) => step.key === job.phase);

  return (
    <section
      role="status"
      aria-live="polite"
      aria-label="Remediation progress"
      className="fixed bottom-6 right-6 z-40 w-[400px] max-w-[calc(100vw-32px)] bg-[#1c1c1c] text-white border border-gray-700 rounded-xl shadow-2xl overflow-hidden"
    >
      <style>{`
        @keyframes remediationProgressShimmer {
          from { background-position: 200% 0; }
          to { background-position: -200% 0; }
        }
      `}</style>

      <div className="px-4 pt-4 pb-3">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold">{summary.title}</h2>
            <p className="text-xs text-gray-400 mt-1">{summary.detail}</p>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? 'Hide details' : 'Show details'}
            aria-expanded={expanded}
            className="p-1 text-gray-400 hover:text-white transition-colors"
          >
            {expanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
          {isDone && (
            <button onClick={onDismiss} aria-label="Dismiss" className="p-1 text-gray-400 hover:text-white transition-colors">
              <X size={16} />
            </button>
          )}
        </div>

        {/* Where we are: Check, Review, Create */}
        <ol className="flex items-center gap-2 mt-3 text-[11px]" aria-label="Remediation steps">
          {STEPS.map((step, index) => {
            const isComplete = index < activeStep;
            const isActive = index === activeStep;
            return (
              <li
                key={step.key}
                aria-current={isActive ? 'step' : undefined}
                className={`flex items-center gap-1 ${isActive ? 'text-white font-medium' : isComplete ? 'text-emerald-400' : 'text-gray-500'}`}
              >
                {isComplete ? (
                  <CheckCircle2 size={12} />
                ) : (
                  <span className={`h-2 w-2 rounded-full ${isActive ? 'bg-red-500' : 'bg-gray-600'}`} />
                )}
                {step.label}
              </li>
            );
          })}
          {summary.unit && (
            <li className="ml-auto text-gray-400">
              {summary.done} of {summary.total} {summary.unit}
            </li>
          )}
        </ol>

        <div
          role="progressbar"
          aria-label={summary.title}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          className="mt-2 h-1.5 w-full bg-zinc-800 rounded-full overflow-hidden"
        >
          <div
            className={`h-full rounded-full transition-all duration-300 ${isDone ? (hasFailures ? 'bg-amber-500' : 'bg-emerald-500') : ''}`}
            style={{
              width: `${percent}%`,
              ...(isDone
                ? {}
                : {
                    backgroundImage: 'linear-gradient(90deg, #b91c1c, #ef4444, #b91c1c)',
                    backgroundSize: '200% 100%',
                    animation: 'remediationProgressShimmer 1.5s linear infinite',
                  }),
            }}
          />
        </div>
      </div>

      {expanded && (
        <ul className="max-h-[220px] overflow-y-auto border-t border-gray-800 divide-y divide-gray-800 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
          {items.map((item) => (
            <li key={item.key} className="flex items-center gap-2 px-4 py-2">
              <span className="flex-shrink-0">{stageIcon(item.stage)}</span>
              <span className="flex-1 min-w-0">
                <span className="block text-xs text-white truncate" title={item.title}>
                  {item.title}
                </span>
                <span className="block text-[11px] text-gray-400 truncate">{item.message || 'Waiting…'}</span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {job.phase === 'checking' && (
        <div className="flex justify-end border-t border-gray-800 px-4 py-2">
          <button onClick={onStop} className="text-xs text-gray-400 hover:text-white transition-colors">
            Stop
          </button>
        </div>
      )}
    </section>
  );
};

export default RemediationProgressTray;
